import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    motion, useScroll, useTransform, useSpring, useInView, AnimatePresence
} from "framer-motion";
import {
    Flame, ArrowRight, Plus, Star, Sparkles, Clock, Beef,
    Leaf, Truck, ChevronRight, Quote, MapPin
} from "lucide-react";
import Lenis from "lenis";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { CinematicNav } from "../components/CinematicNav";
import { CartDrawer } from "../components/CartDrawer";
import { QRScanSection } from "../components/QRScanSection";
import { toast } from "sonner";

// === Smooth scroll bootstrap ===
const useLenis = () => {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
        function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
        const id = requestAnimationFrame(raf);
        return () => { cancelAnimationFrame(id); lenis.destroy(); };
    }, []);
};

// Premium fallback burger imagery (cinematic)
const HERO_IMG = "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=2000&q=80";
const EXPERIENCE_IMGS = [
    "https://images.unsplash.com/photo-1606755962773-d324e0a13086?auto=format&fit=crop&w=1200&q=80",  // grilled patty
    "https://images.unsplash.com/photo-1551782450-a2132b4ba21d?auto=format&fit=crop&w=1200&q=80",   // ingredients
    "https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&w=1200&q=80", // assembly
    "https://images.unsplash.com/photo-1572802419224-296b0aeee0d9?auto=format&fit=crop&w=1200&q=80", // smoke
    "https://images.unsplash.com/photo-1550317138-10000687a72b?auto=format&fit=crop&w=1200&q=80",   // fire grill
];
const REVIEWS = [
    { name: "Νίκος Π.", rating: 5, text: "Άλλο επίπεδο. Smash technique τέλεια, brioche bun ζεστό, sauces θεϊκές.", role: "Foodie · Θεσσαλονίκη" },
    { name: "Ελένη Κ.", rating: 5, text: "Το Truffle burger είναι obsession. Έχω παραγγείλει 12 φορές αυτόν τον μήνα.", role: "Repeat customer" },
    { name: "Γιώργος Α.", rating: 5, text: "Σαν να είσαι σε premium American diner αλλά καλύτερο. Delivery γρηγορότατο.", role: "Verified buyer" },
    { name: "Μαρία Σ.", rating: 5, text: "Δεν είναι απλά burger. Είναι εμπειρία. Από το packaging μέχρι την τελευταία μπουκιά.", role: "Local guide" },
];

export const BurgerHome = () => {
    useLenis();
    const navigate = useNavigate();
    const { add, count } = useCart();
    const [cartOpen, setCartOpen] = useState(false);
    const [store, setStore] = useState(null);

    // Scroll progress
    const { scrollYProgress } = useScroll();
    const progressBar = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });

    useEffect(() => {
        api.get("/stores/grill-bros").then(({ data }) => setStore(data)).catch(() => {});
    }, []);

    const featured = useMemo(() => {
        if (!store) return [];
        const burgers = (store.menu || []).filter((i) => i.category === "burgers");
        return burgers.slice(0, 6);
    }, [store]);

    const handleAdd = (item) => {
        add({ id: item.id, name: item.name, price: item.price, image_url: item.image_url, category: item.category }, { silent: true });
        toast.success(`${item.name} → cart`, { duration: 1800 });
    };

    return (
        <div data-testid="burger-home" className="burger-bg min-h-screen relative overflow-x-hidden">
            {/* Scroll progress */}
            <div className="scroll-progress">
                <motion.div className="bar" style={{ scaleX: progressBar }} />
            </div>

            {/* Cinematic Nav + Cart */}
            <CinematicNav onOpenCart={() => setCartOpen(true)} />
            <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

            {/* ===== HERO ===== */}
            <HeroSection onPrimary={() => navigate("/menu")} onSecondary={() => navigate("/menu")} />

            {/* ===== FEATURED BURGERS ===== */}
            <FeaturedBurgers burgers={featured} onAdd={handleAdd} onMore={() => navigate("/menu")} />

            {/* ===== WHY US ===== */}
            <WhyUsSection />

            {/* ===== EXPERIENCE (horizontal scroll) ===== */}
            <ExperienceSection />

            {/* ===== SOCIAL PROOF ===== */}
            <SocialProofSection />

            {/* ===== QR SCAN TO ORDER ===== */}
            <QRScanSection />

            {/* ===== FINAL CTA ===== */}
            <FinalCTASection onClick={() => navigate("/menu")} />

            {/* Floating mobile cart button */}
            <AnimatePresence>
                {count > 0 && (
                    <motion.button
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        onClick={() => setCartOpen(true)}
                        data-testid="floating-cart-mobile"
                        className="md:hidden fixed bottom-5 left-5 right-5 z-[500] magnet-cta primary h-14"
                    >
                        <Flame size={16} /> Cart · {count} · See order
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Footer */}
            <Footer />
        </div>
    );
};

/* =========================================================
   HERO
   ========================================================= */
const HeroSection = ({ onPrimary, onSecondary }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg     = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const yText    = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
    const opacity  = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    return (
        <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] flex items-center justify-center overflow-hidden">
            {/* Parallax burger image */}
            <motion.div
                style={{ y: yImg }}
                className="absolute inset-0 z-0 scale-110"
            >
                <img
                    src={HERO_IMG}
                    alt=""
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/40 via-black/60 to-black" />
            <div className="smoke-layer" />
            <div className="grain-layer" />

            {/* Headline */}
            <motion.div
                style={{ y: yText, opacity }}
                className="relative z-10 text-center px-5 max-w-5xl mx-auto"
            >
                <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="font-display text-sm tracking-[0.5em] ember-text mb-5"
                    data-testid="hero-eyebrow"
                >
                    EST · 2013 · THESSALONIKI
                </motion.p>
                <h1 data-testid="hero-title" className="font-display text-[14vw] md:text-[8vw] leading-[0.92] tracking-tight mb-2">
                    <AnimatedHeading text="NOT JUST A BURGER." className="block text-white" />
                    <AnimatedHeading text="AN OBSESSION." className="block ember-text" delay={0.25} />
                </h1>
                <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mt-6"
                    data-testid="hero-sub"
                >
                    Premium smash burgers crafted for people who demand more.<br className="hidden sm:block" />
                    Φρέσκα υλικά, brioche buns, signature sauces.
                </motion.p>
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 1.2 }}
                    className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mt-10"
                >
                    <MagneticButton onClick={onPrimary} primary data-testid="hero-cta-primary">
                        <Flame size={16} /> Order Now <ArrowRight size={16} strokeWidth={3} />
                    </MagneticButton>
                    <MagneticButton onClick={onSecondary} data-testid="hero-cta-secondary">
                        View Menu
                    </MagneticButton>
                </motion.div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
            >
                <p className="text-[10px] font-display tracking-[0.4em] text-white/40">SCROLL</p>
                <motion.div
                    animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}
                    className="h-7 w-[2px] bg-gradient-to-b from-white/40 to-transparent"
                />
            </motion.div>
        </section>
    );
};

const AnimatedHeading = ({ text, className = "", delay = 0 }) => (
    <span className={`word-reveal ${className}`}>
        {text.split(" ").map((w, i, arr) => (
            <span key={i} className="inline-block mr-3" style={{ animationDelay: `${delay + i * 0.08}s` }}>
                {w}{i < arr.length - 1 ? "\u00A0" : ""}
            </span>
        ))}
    </span>
);

/* =========================================================
   MAGNETIC BUTTON (hero + final CTA)
   ========================================================= */
const MagneticButton = ({ children, onClick, primary, ...rest }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        setPos({ x: (e.clientX - r.left - r.width / 2) * 0.25, y: (e.clientY - r.top - r.height / 2) * 0.4 });
    };
    return (
        <motion.button
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={() => setPos({ x: 0, y: 0 })}
            animate={{ x: pos.x, y: pos.y }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            onClick={onClick}
            className={`magnet-cta ${primary ? "primary" : "ghost"}`}
            {...rest}
        >
            {children}
        </motion.button>
    );
};

/* =========================================================
   FEATURED BURGERS
   ========================================================= */
const FeaturedBurgers = ({ burgers, onAdd, onMore }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section ref={ref} data-testid="featured" className="relative py-24 sm:py-32 px-5 sm:px-10 max-w-7xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: [0.2,0.9,0.2,1] }}
                className="flex items-end justify-between flex-wrap gap-5 mb-12"
            >
                <div>
                    <p className="font-display text-xs tracking-[0.5em] ember-text mb-2">// THE LINEUP</p>
                    <h2 className="font-display text-5xl sm:text-7xl text-white leading-[0.95]">
                        SIGNATURE<br /><span className="ember-text">BURGERS</span>
                    </h2>
                </div>
                <button onClick={onMore} data-testid="featured-more" className="magnet-cta ghost text-xs h-12 px-5">
                    All Menu <ArrowRight size={14} strokeWidth={3} />
                </button>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {burgers.map((b, i) => (
                    <BurgerCard key={b.id} burger={b} index={i} onAdd={() => onAdd(b)} />
                ))}
            </div>
        </section>
    );
};

const BurgerCard = ({ burger, index, onAdd }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    const [tilt, setTilt] = useState({ x: 0, y: 0 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        const rx = ((e.clientY - r.top - r.height / 2) / r.height) * -12;
        const ry = ((e.clientX - r.left - r.width / 2) / r.width) * 12;
        setTilt({ x: rx, y: ry });
    };

    return (
        <motion.article
            ref={ref}
            initial={{ opacity: 0, y: 60 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, delay: 0.08 * index, ease: [0.2,0.9,0.2,1] }}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            style={{ transformStyle: "preserve-3d", transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
            data-testid={`featured-card-${burger.id}`}
            className="burger-card glass-burger rounded-3xl overflow-hidden group cursor-pointer"
        >
            {/* Image */}
            <div className="relative h-64 overflow-hidden">
                <img
                    src={burger.image_url}
                    alt={burger.name}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
                    <Flame size={11} className="text-[var(--burger-orange-2)]" />
                    <span className="text-[10px] font-display tracking-widest text-white">SIGNATURE</span>
                </div>
                <div className="glow-fade absolute -inset-12 bg-[var(--burger-orange)]/30 blur-3xl rounded-full" />
            </div>

            {/* Body */}
            <div className="p-5 relative" style={{ transform: "translateZ(40px)" }}>
                <h3 className="font-display text-2xl tracking-wide leading-none">{burger.name.toUpperCase()}</h3>
                <p className="text-xs text-white/55 leading-relaxed mt-2 line-clamp-2 min-h-[2.4em]">
                    {burger.description}
                </p>
                <div className="flex items-center justify-between mt-4">
                    <span className="font-display text-3xl ember-text">€{burger.price.toFixed(2)}</span>
                    <motion.button
                        whileTap={{ scale: 0.92 }}
                        onClick={(e) => { e.stopPropagation(); onAdd(); }}
                        data-testid={`add-${burger.id}`}
                        className="h-11 w-11 grid place-items-center rounded-full ember-gradient text-black shadow-[0_8px_24px_rgba(255,138,0,0.4)] hover:scale-110 transition"
                    >
                        <Plus size={18} strokeWidth={3} />
                    </motion.button>
                </div>
            </div>
        </motion.article>
    );
};

/* =========================================================
   WHY US
   ========================================================= */
const PILLARS = [
    { icon: Leaf,  title: "FRESH DAILY",       sub: "Brioche buns και κρέας που φτάνει κάθε πρωί. Ποτέ κατεψυγμένο." },
    { icon: Flame, title: "SMASH TECHNIQUE",   sub: "100% Black Angus σε καυτή πλάκα. Crusted edges, juicy center — every single time." },
    { icon: Truck, title: "ALWAYS FAST",       sub: "Από την παραγγελία στο πιάτο σου σε ~30'. Παράδοση από επαγγελματίες couriers." },
];
const WhyUsSection = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section ref={ref} data-testid="why-us" className="relative py-24 sm:py-32 px-5 sm:px-10">
            <div className="absolute inset-0 -z-0 bg-gradient-to-b from-black via-[#0f0d0a] to-black" />
            <div className="relative max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-16"
                >
                    <p className="font-display text-xs tracking-[0.5em] ember-text mb-3">// WHY GRILL BROS</p>
                    <h2 className="font-display text-5xl sm:text-7xl leading-[0.95]">
                        BUILT FOR<br /><span className="ember-text">BURGER FANATICS</span>
                    </h2>
                </motion.div>
                <div className="grid md:grid-cols-3 gap-5">
                    {PILLARS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 40 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.7, delay: 0.15 * i }}
                                data-testid={`pillar-${i}`}
                                className="glass-burger rounded-3xl p-7 hover:border-[var(--burger-orange)]/40 transition group"
                            >
                                <motion.div
                                    whileHover={{ rotate: 8, scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    className="h-14 w-14 rounded-2xl ember-gradient grid place-items-center mb-5"
                                >
                                    <Icon size={22} strokeWidth={2.5} className="text-black" />
                                </motion.div>
                                <h3 className="font-display text-2xl tracking-wide mb-2">{p.title}</h3>
                                <p className="text-sm text-white/55 leading-relaxed">{p.sub}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* =========================================================
   EXPERIENCE — Horizontal scroll showcase
   ========================================================= */
const ExperienceSection = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const x = useTransform(scrollYProgress, [0, 1], ["8%", "-55%"]);
    return (
        <section ref={ref} data-testid="experience" className="relative py-24 sm:py-32 overflow-hidden">
            <div className="max-w-7xl mx-auto px-5 sm:px-10 mb-12">
                <p className="font-display text-xs tracking-[0.5em] ember-text mb-3">// THE CRAFT</p>
                <h2 className="font-display text-5xl sm:text-7xl leading-[0.95]">
                    FIRE.<br /> SIZZLE.<br /><span className="ember-text">PERFECTION.</span>
                </h2>
            </div>
            <motion.div style={{ x }} className="flex gap-5 px-5 sm:px-10 will-change-transform">
                {EXPERIENCE_IMGS.map((src, i) => (
                    <div key={i} className="shrink-0 w-[80vw] sm:w-[36vw] h-[60vh] rounded-3xl overflow-hidden relative group" data-testid={`exp-${i}`}>
                        <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        <p className="absolute bottom-6 left-6 font-display text-3xl tracking-tight text-white">
                            {["GRILLED.", "FRESH.", "STACKED.", "HOT.", "EMBER."][i]}
                        </p>
                    </div>
                ))}
            </motion.div>
        </section>
    );
};

/* =========================================================
   SOCIAL PROOF
   ========================================================= */
const SocialProofSection = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section ref={ref} data-testid="social-proof" className="relative py-24 sm:py-32 px-5 sm:px-10 max-w-6xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7 }}
                className="text-center mb-14"
            >
                <p className="font-display text-xs tracking-[0.5em] ember-text mb-3">// THE FANS</p>
                <h2 className="font-display text-5xl sm:text-7xl">
                    LOVED BY<br /><span className="ember-text">THESSALONIKI</span>
                </h2>
                <div className="flex items-center justify-center gap-1 mt-6">
                    {[1,2,3,4,5].map((s) => (
                        <Star key={s} size={20} fill="#FFB347" className="text-[#FFB347]" />
                    ))}
                    <span className="ml-3 text-sm text-white/65 font-mono">4.9 · 1,200+ reviews</span>
                </div>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-4">
                {REVIEWS.map((r, i) => (
                    <motion.article
                        key={i}
                        initial={{ opacity: 0, y: 40 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.7, delay: 0.1 * i }}
                        data-testid={`review-${i}`}
                        className="glass-burger rounded-3xl p-6 relative"
                    >
                        <Quote size={28} className="absolute top-5 right-5 text-[var(--burger-orange)]/20" />
                        <div className="flex gap-1 mb-3">
                            {Array(r.rating).fill(0).map((_, j) => (
                                <Star key={j} size={13} fill="#FFB347" className="text-[#FFB347]" />
                            ))}
                        </div>
                        <p className="text-base text-white/85 leading-relaxed">"{r.text}"</p>
                        <div className="mt-4 flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full ember-gradient grid place-items-center font-display text-black text-sm">
                                {r.name[0]}
                            </div>
                            <div>
                                <p className="font-display text-sm tracking-wider">{r.name.toUpperCase()}</p>
                                <p className="text-[10px] text-white/45">{r.role}</p>
                            </div>
                        </div>
                    </motion.article>
                ))}
            </div>
        </section>
    );
};

/* =========================================================
   FINAL CTA
   ========================================================= */
const FinalCTASection = ({ onClick }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-10%", "20%"]);
    return (
        <section ref={ref} data-testid="final-cta" className="relative h-[80svh] min-h-[500px] overflow-hidden grid place-items-center">
            <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
                <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            </motion.div>
            <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/60 via-black/70 to-black" />
            <div className="smoke-layer" />
            <div className="grain-layer" />
            <div className="relative z-10 text-center max-w-3xl mx-auto px-5">
                <h2 className="font-display text-5xl sm:text-8xl leading-[0.95] text-white">
                    READY FOR THE<br />
                    <span className="ember-text">BEST BURGER</span><br />
                    OF YOUR LIFE?
                </h2>
                <p className="text-white/65 mt-6 max-w-md mx-auto">
                    Έλα να σου δείξουμε γιατί δεν ξαναγυρίζεις σε τίποτα άλλο.
                </p>
                <div className="mt-10 flex justify-center">
                    <MagneticButton onClick={onClick} primary data-testid="final-cta-btn">
                        <Flame size={16} /> Order Now <ArrowRight size={16} strokeWidth={3} />
                    </MagneticButton>
                </div>
            </div>
        </section>
    );
};

/* =========================================================
   FOOTER
   ========================================================= */
const Footer = () => {
    const navigate = useNavigate();
    return (
        <footer className="px-5 sm:px-10 py-12 border-t border-white/5">
            <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-8">
                <div>
                    <p className="font-display text-3xl tracking-[0.18em] ember-text">GRILL BROS</p>
                    <p className="text-xs text-white/45 mt-2 leading-relaxed">
                        Est. 2013 · Premium smash burgers handcrafted in the heart of Thessaloniki. Φρέσκο κάθε μέρα.
                    </p>
                </div>
                <div>
                    <p className="text-[10px] font-display tracking-[0.4em] text-white/45 mb-3">// LOCATION</p>
                    <p className="text-sm text-white/85 flex items-center gap-2">
                        <MapPin size={13} className="text-[var(--burger-orange-2)]" />
                        Ελ. Βενιζέλου 72, Θεσσαλονίκη 54631
                    </p>
                    <p className="text-sm text-white/85 mt-2">+30 231 022 5855</p>
                    <p className="text-xs text-white/45 mt-2">Δευτ–Πέμ 12:00 · Παρ–Κυρ 12:00–01:00</p>
                </div>
                <div>
                    <p className="text-[10px] font-display tracking-[0.4em] text-white/45 mb-3">// EXPLORE</p>
                    <div className="flex flex-col items-start gap-1.5">
                        <button onClick={() => navigate("/menu")} className="text-sm text-white/75 hover:text-[var(--burger-orange-2)] transition">Menu</button>
                        <button onClick={() => navigate("/marketplace")} className="text-sm text-white/75 hover:text-[var(--burger-orange-2)] transition">Explore Thessaloniki Stores →</button>
                        <button onClick={() => navigate("/orders")} className="text-sm text-white/75 hover:text-[var(--burger-orange-2)] transition">Your orders</button>
                    </div>
                </div>
            </div>
            <p className="text-center text-[10px] text-white/30 mt-10 font-mono tracking-widest">
                © {new Date().getFullYear()} GRILL BROS · CRAFTED IN THESSALONIKI
            </p>
        </footer>
    );
};

export default BurgerHome;
